import { useState } from 'react';
import { Sparkles, ChevronDown } from 'lucide-react';

/**
 * Componente de seleção de modelo de IA
 * Permite ao usuário escolher diferentes modelos para processar a imagem
 */
const ModelSelector = ({ currentModel, onModelChange, isProcessing }) => {
  const [isOpen, setIsOpen] = useState(false);

  const models = [
    {
      id: 'u2net',
      name: 'U2Net',
      description: 'Universal - melhor qualidade geral',
      icon: '🎯'
    },
    {
      id: 'u2netp',
      name: 'U2Net (Rápido)',
      description: 'Menor tamanho, processamento mais rápido',
      icon: '⚡'
    },
    {
      id: 'u2net_human_seg',
      name: 'Pessoas',
      description: 'Otimizado para retratos e pessoas',
      icon: '👤'
    },
    {
      id: 'u2net_cloth_seg',
      name: 'Roupas',
      description: 'Segmenta vestuário e tecidos',
      icon: '👕'
    },
    {
      id: 'silueta',
      name: 'Silueta',
      description: 'Contorno simples e básico',
      icon: '🎭'
    },
    {
      id: 'isnet-general-use',
      name: 'ISNet',
      description: 'Última geração - máxima qualidade',
      icon: '✨'
    },
    {
      id: 'isnet-anime',
      name: 'ISNet Anime',
      description: 'Otimizado para anime e cartoon',
      icon: '🎨'
    },
    {
      id: 'rmbg-1.4',
      name: 'BRIA RMBG 1.4',
      description: 'Alta precisão - modelo BRIA AI',
      icon: '🚀'
    }
  ];

  const selectedModel = models.find(m => m.id === currentModel) || models[0];

  const handleModelSelect = (modelId) => {
    if (modelId !== currentModel && !isProcessing) {
      onModelChange(modelId);
      setIsOpen(false);
    }
  };

  return (
    <div className="relative">
      {/* Botão principal */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isProcessing}
        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Sparkles className="w-4 h-4 text-purple-500" />
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <circle cx="12" cy="12" r="9" strokeWidth="2" />
          </svg>
          <div className="text-left">
            <div className="text-sm font-medium text-gray-900">
              {selectedModel.name}
            </div>
            <div className="text-xs text-gray-500">
              Modelo de IA
            </div>
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Menu */}
          <div className="absolute top-full left-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-xl z-20 max-h-96 overflow-y-auto">
            <div className="p-2">
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
                Selecione um modelo de IA
              </div>
              {models.map((model) => (
                <button
                  key={model.id}
                  onClick={() => handleModelSelect(model.id)}
                  className={`w-full flex items-start gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    model.id === currentModel
                      ? 'bg-purple-50 border border-purple-200'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <svg className="w-5 h-5 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <circle cx="12" cy="12" r="9" strokeWidth="2" />
                  </svg>
                  <div className="flex-1 text-left">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">
                        {model.name}
                      </span>
                      {model.id === currentModel && (
                        <span className="px-2 py-0.5 text-xs font-medium text-purple-700 bg-purple-100 rounded">
                          Em uso
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {model.description}
                    </p>
                  </div>
                </button>
              ))}
            </div>

            <div className="border-t border-gray-100 p-3 bg-gray-50">
              <p className="text-xs text-gray-600">
                💡 <strong>Dica:</strong> Experimente diferentes modelos para obter o melhor resultado!
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ModelSelector;
